import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { useAddAttendee } from "../../hooks/useAttendee";
import { toast } from "react-toastify";
import axios from "axios";
import doctorMasterDataService from "../../services/doctorMasterDataService";

// Only checks if an ID exists in the form data.

// Does not check Firestore for an existing doctor with the same email, national_id, or APO_number.

// Result: multiple documents for the same doctor can exist → duplicates.

// after
// Always queries Firestore first, looking for an existing doctor by unique identifiers.

// Only creates a new doctor if none exists.

// Result: one doctor per unique email / national_id / APO_number.


function TermsSignature({ values, setValues, setCurrentStep, eventData }) {
  const sigCanvas = useRef();
  const { mutate: addAttendee } = useAddAttendee();
  const [showSpinning, setShowSpinning] = useState(false);

  const clear = () => {
    sigCanvas.current.clear();
    localStorage.removeItem("savedSignature");
  };

  // Load saved signature from localStorage
  const loadSavedSignature = () => {
    const savedSignature = localStorage.getItem("savedSignature");
    if (savedSignature && sigCanvas.current) {
      sigCanvas.current.fromDataURL(savedSignature);
    }
  };
  loadSavedSignature();

  // Save signature to localStorage
  const saveSignatureToLocalStorage = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL("image/png");
      localStorage.setItem("savedSignature", signatureData);
    }
  };

  // Handle window resize to preserve signature
  useEffect(() => {
    let resizeTimeout;

    const handleResize = () => {
      // Clear any existing timeout
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      // Save current signature before resize
      saveSignatureToLocalStorage();

      // Restore signature after resize with a small delay
      resizeTimeout = setTimeout(() => {
        loadSavedSignature();
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, []);

  // Save signature to localStorage as user draws
  useEffect(() => {
    const signatureCanvas = sigCanvas.current;
    if (!signatureCanvas) return;

    // Attach event listeners to save signature after drawing
    const canvas = signatureCanvas.getCanvas();

    const handleDrawEnd = () => {
      saveSignatureToLocalStorage();
    };

    canvas.addEventListener("mouseup", handleDrawEnd);
    canvas.addEventListener("touchend", handleDrawEnd);

    return () => {
      canvas.removeEventListener("mouseup", handleDrawEnd);
      canvas.removeEventListener("touchend", handleDrawEnd);
    };
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (showSpinning) return;
    setShowSpinning(true);
    try {
      const signatureCanvas = sigCanvas.current;
      if (!signatureCanvas) {
        toast.error("Signature pad not initialized");
        setShowSpinning(false);
        return;
      }
      //  Check if user actually signed
      if (signatureCanvas.isEmpty()) {
        toast.error("Signature is required");
        setShowSpinning(false);
        return;
      }
      const signatureData = signatureCanvas.toDataURL("image/png");
      // Upload signature
      const { data, status } = await axios.post(
        `${process.env.REACT_APP_API_URL}/upload-signature`,
        {
          nationalId: values.national_id,
          eventID: values.event_id,
          sign64data: signatureData,
        }
      );
      if (status !== 200 || !data?.url)
        toast.error("Failed to upload signature");
      const signatureUrl = data.url;
      // Ensure doctor master data exists
      const doctorMasterDataId = await doctorMasterDataService.getOrCreateDoctorMasterData(values);

      const finalAttendeeData = {
        ...values,
        signature: signatureUrl,
        sign64data: signatureData,
        doctor_master_data_id: doctorMasterDataId,
        created_at: values.created_at || new Date().toISOString(),
      };
      // Save and submit attendee
      setValues(finalAttendeeData);
      addAttendee(finalAttendeeData);
      toast.success("Thank you for registering!");
      localStorage.removeItem("savedSignature");
      // Reset form but keep event_id
      setValues((prev) => ({
        event_id: prev.event_id,
        first_name: "",
        family_name: "",
        national_id: "",
        email: "",
        phone_number: "",
        country_id: "",
        city_id: "",
        organization: "",
        specialty: "",
        APO_number: "",
        medical_license: "",
        signature: "",
        sign64data: "",
        doctor_master_data_id: "",
        attendee_type: "",
        updated_at: null,
        created_at: null,
      }));
      // Go back 2 steps after success
      // setShowSpinning(false);
      setTimeout(() => {
        setCurrentStep((prev) => prev - 2);
      }, 500);
    } catch (error) {
      toast.error(error.message || "Failed to register attendee");
    }
  };

  const handleBack = () => {
    setCurrentStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <div className="container">
      <div className="py-10 bg-white rounded-lg shadow mx-5 md:mx-8 lg:mx-0">
        <div className="w-[80%] md:w-3/4 mx-auto">
          <div className="mb-5">
            <h1
              style={{
                color: eventData?.font_color,
              }}
              className="font-bold text-2xl "
            >
              Signature
            </h1>
            <p className="my-2 text-gray-500 text-sm">Please Sign Below:</p>
            <div className="grid grid-cols-1">
              <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{ className: "border w-full h-[200px]" }}
              />
            </div>
            <div className="flex justify-center mt-4">
              <button
                className="border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-all duration-300 px-4 py-2 rounded-lg font-semibold mt-3 flex items-center gap-2"
                onClick={clear}
              >
                <i className="fa-solid fa-trash text-sm"></i>
                Clear Signature
              </button>
            </div>
          </div>
          <div className="mt-8">
            <h1
              style={{
                color: eventData?.font_color,
              }}
              className="font-bold text-2xl"
            >
              Terms and Conditions
            </h1>
            <div className="py-4">
              <h6 className="text-gray-600 font-semibold">
                Due to the Financial Transparency and Disclosure guideline of -
                SFDA
              </h6>
              <p className="text-gray-600">
                It is obliged to document any Transfer of Value given to
                healthcare professionals and healthcare organizations and to
                publish them online.
              </p>
              <h6 className="text-gray-600 font-semibold">
                By accepting this,
              </h6>
              <p className="text-gray-600">
                I hereby certify that all the information provided by me is true
                and accurate and I accept and understand that my data may be
                transferred for the purposes described above, to other Abbott
                group companies based in the United States of America.
              </p>
              <a
                href="https://www.arabia.abbott/en/privacy-policy.html"
                target="_blank"
                className="border-none bg-white text-blue-600 font-semibold text-left underline"
              >
                Abbott's Privacy Notice
              </a>
            </div>
          </div>

          <div className="flex justify-between mt-5">
            <button
              className="border border-blueColor text-blueColor hover:bg-blueColor hover:text-white transition-all duration-300 px-6 py-2 lg:w-48 rounded-lg font-semibold"
              onClick={handleBack}
            >
              Back
            </button>
            <button
              data-track="submit-attendee-register-button"
              data-module="Register"
              type="submit"
              style={{
                backgroundColor: eventData?.btn_color,
              }}
              className={`active:scale-[.98] active:duration-75 hover:scale-[1.01] ease-in-out transition-all px-6 py-2 lg:py-3 rounded-lg 
                    text-white lg:font-bold flex justify-center items-center gap-3 lg:w-48 ${
                      showSpinning ? "opacity-80 cursor-not-allowed" : ""
                    }`}
              onClick={handleSubmit}
              disabled={showSpinning}
            >
              Submit
              {showSpinning && (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsSignature;